import React from 'react';
import logo from '../images/Globe-Memories.png';
import { useLanguage } from '../context/LanguageContext';

export const STARTUP_SPLASH_DURATION_MS = 3760;

const FLIGHT_PATH = 'M-220 665C140 650 320 425 525 390S935 325 1460 210';
const STARS = [
  [285, 188, 1.8], [401, 246, 1.1], [502, 151, 1.5], [665, 210, 1.2],
  [805, 155, 1.7], [891, 290, 1.3], [351, 355, 1], [750, 319, 1],
  [1010, 221, 1.2], [216, 306, 1.1], [571, 287, 1], [1072, 390, 1.6],
];

const Cloud = ({ x, y, scale = 1 }) => (
  <g transform={'translate(' + x + ' ' + y + ') scale(' + scale + ')'}>
    <path fill="#eff8ff" d="M0 28C-9 12 6 0 22 6C25-21 64-25 76-1C92-13 116-1 114 17C137 8 158 21 155 34H0Z" />
    <path fill="#aedcf1" d="M0 28C12 18 26 22 31 28C34 11 60 7 71 23C84 15 96 20 101 30C119 20 142 23 155 34H0Z" />
  </g>
);

const Aircraft = () => (
  <g className="gm-splash-screen__aircraft-flight">
    <animateMotion
      path={FLIGHT_PATH}
      begin="0.12s"
      dur="2.02s"
      rotate="auto"
      calcMode="spline"
      keyPoints="0;1"
      keyTimes="0;1"
      keySplines="0.45 0 0.65 1"
      fill="freeze"
    />
    <g className="gm-splash-screen__aircraft">
      <g fill="none" stroke="#e3f5ff" strokeLinecap="round">
        <path d="M-228 8H-153" strokeWidth="3" opacity="0.4" />
        <path d="M-202 20H-150" strokeWidth="2" opacity="0.24" />
      </g>
      <path fill="#c2e5f6" d="M2-10L-58-70L-80-68L-38-6Z" />
      <path fill="#ff9900" d="M-117 8L-155-71Q-159-80-164-73L-151 17Z" />
      <path fill="#fff" d="M-148 10L-126-10Q-119-15-103-14L91-14Q125-15 147 4Q163 17 139 24L-110 31Q-137 30-148 10Z" />
      <path fill="#d6edf7" d="M-142 19Q-111 24-69 23L137 16Q152 14 150 11Q160 20 139 24L-110 31Q-130 31-142 19Z" />
      <path fill="#ff9900" d="M-137 13Q-82 24 0 12L109-5Q129-5 142 4Q79 8 29 21Q-85 42-137 13Z" />
      <path fill="#006ab9" d="M116-9L128-6L136 0L117-1Z" />
      <g fill="#28769e">
        {[-84, -71, -58, -45, -32, -19, -6, 7, 20, 33, 46, 59, 72, 85].map((x) => (
          <rect key={x} x={x} y="-7" width="5" height="5" rx="1.5" />
        ))}
      </g>
      <path fill="#fff" d="M-13 13L-52 84Q-54 92-42 94L-25 92L60 19Z" />
      <path fill="#cae6f1" d="M-44 85L-26 84L60 19L-25 92L-42 94Q-51 92-44 85Z" />
      <path fill="#ff9900" d="M10 36Q21 25 47 30L59 43Q59 54 38 58Q18 59 10 49Z" />
      <ellipse cx="54" cy="44" rx="7" ry="11" fill="#0e5078" />
      <ellipse cx="54" cy="44" rx="3" ry="6" fill="#79b8d5" />
      <path fill="#f1faff" d="M-119 19L-156 40L-139 42L-89 25Z" />
      <path fill="none" stroke="#fff2d9" strokeWidth="3" strokeLinecap="round" d="M-149-59L-132-26" />
      <text x="-65" y="10" fill="#006ab9" fontSize="9" fontWeight="800" letterSpacing="1.3">GLOBE</text>
    </g>
  </g>
);

const SplashScreen = ({ isLaunch = false, closing = false }) => {
  const { t } = useLanguage();

  // Routine loading states stay quiet; the journey belongs to a fresh launch.
  if (!isLaunch) {
    return (
      <div className="gm-splash-screen gm-splash-screen--loading" role="status" aria-label={t('A preparar...')}>
        <img className="gm-splash-screen__loading-logo" src={logo} alt="Globe Memories" />
        <p className="gm-splash-screen__loading-caption">{t('A preparar...')}</p>
        <span className="gm-splash-screen__loading-line" aria-hidden="true"><span /></span>
      </div>
    );
  }

  return (
    <div
      className={`gm-splash-screen gm-splash-screen--launch${closing ? ' gm-splash-screen--closing' : ''}`}
      role="status"
      aria-label={t('A preparar...')}
    >
      <div className="gm-splash-screen__sky" aria-hidden="true" />
      <div className="gm-splash-screen__sun" aria-hidden="true" />

      {/* A matching foreground circle carries the sun across the horizon. */}
      <div className="gm-splash-screen__sunrise">
        <div className="gm-splash-screen__brand">
          <img className="gm-splash-screen__logo" src={logo} alt="Globe Memories" loading="eager" decoding="sync" fetchPriority="high" />
          <span className="gm-splash-screen__signature" aria-hidden="true" />
        </div>
      </div>

      <svg className="gm-splash-screen__landscape" viewBox="0 0 1200 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="gm-opening-earth" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#198dc8" />
            <stop offset="0.5" stopColor="#006ab9" />
            <stop offset="1" stopColor="#064978" />
          </linearGradient>
          <linearGradient id="gm-opening-reflection" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffcc7d" stopOpacity="0.35" />
            <stop offset="1" stopColor="#ffcc7d" stopOpacity="0" />
          </linearGradient>
          <clipPath id="gm-opening-earth-clip"><ellipse cx="600" cy="1200" rx="1020" ry="630" /></clipPath>
        </defs>

        <g className="gm-splash-screen__stars" fill="#def3ff">
          {STARS.map(([x, y, r]) => <circle key={x + '-' + y} cx={x} cy={y} r={r} />)}
          <path d="M441 191v10m-5-5h10M841 353v8m-4-4h8" fill="none" stroke="#def3ff" strokeWidth="1.2" />
        </g>

        <g className="gm-splash-screen__clouds gm-splash-screen__clouds--far" opacity="0.7">
          <Cloud x={162} y={548} scale={1.12} />
          <Cloud x={823} y={520} scale={1.6} />
          <Cloud x={1090} y={562} scale={0.8} />
        </g>

        <g className="gm-splash-screen__earth">
          <ellipse cx="600" cy="1200" rx="1020" ry="630" fill="url(#gm-opening-earth)" />
          <g clipPath="url(#gm-opening-earth-clip)">
            <path fill="#6bbcd7" opacity="0.3" d="M258 638l87-24 76 24 21 42-40 20-22 49-43-8-27 31-46-21-29-66zM385 796l65-26 47 31-7 68-28 77-35 49-24-45 2-56-32-42zM642 620l44-26 43 22-2 29 57 13 35 37-23 31-43-7-20 55-39 8-47-55-46-16 8-36-15-21zM833 689l57-40 121 36 32 54-57 15-30 47-72-18-35-34zM874 851l72-15 47 50-17 42-70-5-40-33z" />
            <g fill="none" stroke="#9cdbed" strokeWidth="1" opacity="0.23">
              <ellipse cx="600" cy="1200" rx="740" ry="630" />
              <ellipse cx="600" cy="1200" rx="370" ry="630" />
              <path d="M600 570v470M-80 665Q600 560 1280 665M-240 790Q600 588 1440 790M-330 957Q600 703 1530 957" />
            </g>
            <path d="M568 581h64l34 17-22 16 45 19-23 20 26 30-35 30 31 39-52 118h-80l-52-118 31-39-35-30 26-30-23-20 45-19-22-16z" fill="url(#gm-opening-reflection)" />
            <g fill="none" stroke="#bceaff" strokeWidth="2" opacity="0.16">
              <path d="M504 642h192M463 681h268M429 734h347M397 802h420" />
            </g>
          </g>
          <ellipse cx="600" cy="1200" rx="1020" ry="630" fill="none" stroke="#7ccce7" strokeWidth="2" opacity="0.6" />
        </g>

        <g className="gm-splash-screen__clouds gm-splash-screen__clouds--near">
          <Cloud x={305} y={557} scale={1} />
          <Cloud x={733} y={567} scale={0.8} />
          <Cloud x={992} y={550} scale={1.18} />
        </g>
      </svg>

      <svg className="gm-splash-screen__flight" viewBox="0 0 1200 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
        <path className="gm-splash-screen__flight-route" d={FLIGHT_PATH} pathLength="1" />
        <Aircraft />
      </svg>
    </div>
  );
};

export default SplashScreen;
